import './GlossaryModal.scss';
import cn from 'classnames';
import { ReactNode, useId, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useFilteredList } from '@src/utils/search';
import { Design, Size } from '../designConstants';
import { SearchField } from '../SearchField/SearchField';

export interface GlossaryItem {
  term: string;
  definition: ReactNode;
}

interface GlossaryModalProps {
  show: boolean;
  setShow: (value: boolean) => void;
  title: string;
  initialGlossary: GlossaryItem[];
  modalClassname?: string;
}

// `definition` is ReactNode — only match against it when it's a string.
const definitionKey = (item: GlossaryItem): string | undefined =>
  typeof item.definition === 'string' ? item.definition : undefined;

export const GlossaryModal = ({
  show,
  setShow,
  title,
  initialGlossary,
  modalClassname = ''
}: GlossaryModalProps) => {
  const [query, setQuery] = useState('');
  const searchId = useId();

  // Pre-sort the source alphabetically once. useFilteredList's rank sort is
  // stable, so with no query we get alphabetical order via the empty-query
  // fast path, and with a query rank wins with alphabetical tie-breaks.
  const sortedGlossary = useMemo(
    () => [...initialGlossary].sort((a, b) => a.term.localeCompare(b.term)),
    [initialGlossary]
  );

  const filteredGlossary = useFilteredList(sortedGlossary, query, {
    keys: ['term', definitionKey]
  });

  return (
    <Modal
      show={show}
      centered
      onHide={() => {
        setQuery('');
        setShow(false);
      }}
      size='lg'
      className={cn('glossary-container', { [modalClassname]: modalClassname })}
    >
      <Modal.Header closeButton>
        <div className='modal-title'>{title}</div>
      </Modal.Header>
      <Modal.Body>
        <div className='glossary-filter-container'>
          <SearchField
            size={Size.LG}
            id={searchId}
            design={Design.NEUTRAL}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder='Search'
            aria-label='Search glossary'
          />
        </div>
        <div className='glossary-content'>
          {filteredGlossary.map(({ term, definition }) => (
            <div key={term} className='section'>
              <div className='term'>{term}</div>
              <div className='definition'>{definition}</div>
            </div>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
};
